import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, Check, AlertTriangle, Lock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Step1Personal from './steps/Step1Personal';
import Step2Executors from './steps/Step2Executors';
import Step3AssetCategories from './steps/Step3AssetCategories';
import Step4Assets from './steps/Step4Assets';
import Step5Beneficiaries from './steps/Step5Beneficiaries';
import Step6Exclusions from './steps/Step6Exclusions';
import Step7Residual from './steps/Step7Residual';
import Step8SpecialWishes from './steps/Step8SpecialWishes';
import Step9Storage from './steps/Step9Storage';
import YouTubeEmbed from './YouTubeEmbed';
import WillProcessingModal from './WillProcessingModal';
import PrePaymentDisclaimerModal from './PrePaymentDisclaimerModal';
import PartnerWillPaymentModal from '../payment/PartnerWillPaymentModal';
import { Button } from '../ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PartnerWillCreation = () => {
  const { willId } = useParams();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);
  const [highestStepReached, setHighestStepReached] = useState(1);
  const [willData, setWillData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [videoSettings, setVideoSettings] = useState([]);
  const [isExpired, setIsExpired] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState(null);
  const [expiryDate, setExpiryDate] = useState(null);
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Payment flow states
  const [isWillPaid, setIsWillPaid] = useState(true);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [disclaimerText, setDisclaimerText] = useState('');
  const [partnerPricing, setPartnerPricing] = useState(null);

  const steps = [
    { number: 1, title: 'About You & Family', component: Step1Personal },
    { number: 2, title: 'Executors & Witnesses', component: Step2Executors },
    { number: 3, title: 'Asset Categories', component: Step3AssetCategories },
    { number: 4, title: 'Define Assets', component: Step4Assets },
    { number: 5, title: 'Beneficiaries', component: Step5Beneficiaries },
    { number: 6, title: 'Exclusions', component: Step6Exclusions },
    { number: 7, title: 'Residual Assets', component: Step7Residual },
    { number: 8, title: 'Special Wishes', component: Step8SpecialWishes },
    { number: 9, title: 'Declaration & Storage', component: Step9Storage }
  ];

  const getAuthHeader = () => {
    const token = localStorage.getItem('partnerToken');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    if (willId) fetchWillData();
  }, [willId]);

  useEffect(() => {
    const fetchVideoSettings = async () => {
      try {
        const response = await axios.get(`${API}/videos/will-steps`);
        setVideoSettings(response.data.videos || []);
      } catch (error) {
        // Silent fail
      }
    };
    fetchVideoSettings();
  }, []);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await axios.get(`${API}/partner/will-pricing`, {
          headers: getAuthHeader()
        });
        setPartnerPricing(response.data);
      } catch (error) {
        console.error('Error fetching pricing:', error);
      }
    };
    fetchPricing();
  }, []);

  useEffect(() => {
    const fetchDisclaimer = async () => {
      try {
        const response = await axios.get(`${API}/legals/disclaimer`);
        setDisclaimerText(response.data.disclaimer_text || '');
      } catch (error) {
        setDisclaimerText('By proceeding with payment, you acknowledge that you have reviewed all the information provided and understand that this will document is being created based on the details you have entered.');
      }
    };
    fetchDisclaimer();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  useEffect(() => {
    if (currentStep > highestStepReached) {
      setHighestStepReached(currentStep);
    }
  }, [currentStep, highestStepReached]);

  const fetchWillData = async () => {
    try {
      const response = await axios.get(`${API}/partner/will/${willId}`, {
        headers: getAuthHeader()
      });
      setWillData(response.data);
      const savedStep = response.data.progress?.current_step || response.data.current_step || 1;
      const highestStep = response.data.progress?.highest_step_reached || savedStep;

      const expired = response.data.is_expired || false;
      setIsExpired(expired);
      setDaysRemaining(response.data.days_remaining);
      setExpiryDate(response.data.expiry_date_formatted);

      const isPaid = response.data.is_paid || response.data.payment_status === 'completed';
      setIsWillPaid(isPaid);

      if (expired) {
        setCurrentStep(9);
        setHighestStepReached(9);
        toast.warning('This will has expired. Only Declaration & Storage is accessible.');
      } else {
        setCurrentStep(savedStep);
        setHighestStepReached(highestStep);
      }
    } catch (error) {
      console.error('Error fetching will:', error);
      toast.error('Failed to load will data');
      navigate('/partner/wills');
    } finally {
      setLoading(false);
    }
  };

  const saveStep = async (stepData, advanceStep = true) => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/partner/will/${willId}/step/${currentStep}?advance=${advanceStep}`,
        stepData,
        { headers: getAuthHeader(), timeout: 30000 }
      );
      setWillData({ ...willData, ...stepData });
      return true;
    } catch (error) {
      console.error('Error saving:', error);
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Save timed out. Please check your connection and try again.');
      } else if (error.response?.status === 403) {
        toast.error(error.response.data?.detail || 'Access denied. Will may have expired.');
      } else if (error.response?.status === 404) {
        toast.error('Will not found. Please refresh the page.');
      } else if (error.response?.status >= 500) {
        toast.error('Server error. Please try again in a moment.');
      } else {
        toast.error('Failed to save progress. Please try again.');
      }
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async (stepData) => {
    if (isTransitioning || saving) return;

    const saved = await saveStep(stepData, true);
    if (saved && currentStep < 9) {
      if (currentStep === 8 && !isExpired) {
        setIsTransitioning(true);
        if (!isWillPaid) {
          setShowDisclaimerModal(true);
        } else {
          setShowProcessingModal(true);
        }
      } else {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handleDisclaimerAccept = () => {
    setShowDisclaimerModal(false);
    setShowPaymentModal(true);
  };

  const handleDisclaimerClose = () => {
    setShowDisclaimerModal(false);
    setIsTransitioning(false);
  };

  const handlePaymentSuccess = (data) => {
    setShowPaymentModal(false);
    setIsWillPaid(true);
    setShowProcessingModal(true);
  };

  const handlePaymentClose = () => {
    setShowPaymentModal(false);
    setIsTransitioning(false);
    toast.info('You can complete payment later to access the final step.');
  };

  const handleProcessingComplete = () => {
    setShowProcessingModal(false);
    setIsTransitioning(false);
    setCurrentStep(9);
  };

  const handlePrevious = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSaveAndExit = async (stepData) => {
    const saved = await saveStep(stepData, false);
    if (saved) {
      toast.success('Progress saved! You can continue later.');
      navigate('/partner/wills');
    }
  };

  const handleUpdate = async (stepData) => {
    await saveStep(stepData, false);
  };

  const handleStepClick = (stepNumber) => {
    if (isExpired && stepNumber !== 9) {
      toast.error('This will has expired. Only Declaration & Storage is accessible.');
      return;
    }
    if (stepNumber <= highestStepReached) {
      setCurrentStep(stepNumber);
    }
  };

  const isStepAccessible = (stepNumber) => {
    if (isExpired) return stepNumber === 9;
    return stepNumber <= highestStepReached;
  };

  const getStepStyle = (stepNumber) => {
    if (isExpired) {
      if (stepNumber === 9) return 'bg-amber-500 text-slate-900 border-2 border-amber-500';
      return 'bg-red-900/30 border-2 border-red-500/30 text-red-400/50';
    }
    if (stepNumber < currentStep && stepNumber < highestStepReached) {
      return 'bg-amber-500/20 border-2 border-amber-500 text-amber-400';
    } else if (stepNumber === currentStep) {
      return 'bg-amber-500 text-slate-900 border-2 border-amber-500';
    } else if (stepNumber <= highestStepReached) {
      return 'bg-amber-500/20 border-2 border-amber-500/50 text-amber-400';
    }
    return 'bg-slate-800 border-2 border-slate-700 text-slate-500';
  };

  const getCurrentStepVideo = () => {
    return videoSettings.find(v => v.step_number === currentStep);
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4" data-testid="partner-will-loading">
        <Loader2 className="w-12 h-12 text-amber-400 animate-spin" />
        <p className="text-slate-400">Loading Will...</p>
      </div>
    );
  }

  const CurrentStepComponent = steps[currentStep - 1].component;

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="partner-will-creation">
      {/* Expiry Warning Banner */}
      {isExpired && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-red-400 font-semibold">Will Access Expired</h3>
            <p className="text-red-300/80 text-sm mt-1">
              Access to edit this will expired on {expiryDate}. Only the Declaration & Storage section is available.
            </p>
          </div>
        </div>
      )}

      {/* Days Remaining Warning */}
      {!isExpired && daysRemaining !== null && daysRemaining <= 30 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-amber-400 font-semibold">Access Expiring Soon</h3>
            <p className="text-amber-300/80 text-sm mt-1">
              Access expires in <strong>{daysRemaining} day{daysRemaining !== 1 ? 's' : ''}</strong> ({expiryDate}).
            </p>
          </div>
        </div>
      )}

      {/* Progress Bar - Desktop */}
      <div className="hidden md:block bg-slate-900/50 border border-slate-800 rounded-xl p-4 overflow-x-auto">
        <div className="flex justify-between min-w-[700px] gap-2">
          {steps.map((step) => {
            const isClickable = isStepAccessible(step.number);
            const isCompleted = step.number < currentStep && !isExpired;
            const isLocked = isExpired && step.number !== 9;
            return (
              <div
                key={step.number}
                className={`flex flex-col items-center gap-2 transition-all ${isClickable ? 'cursor-pointer hover:scale-105' : 'cursor-not-allowed'} ${isClickable ? 'opacity-100' : 'opacity-50'}`}
                onClick={() => handleStepClick(step.number)}
                data-testid={`partner-step-indicator-${step.number}`}
              >
                <div className={`w-9 h-9 lg:w-10 lg:h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${getStepStyle(step.number)}`}>
                  {isLocked ? <Lock size={14} /> : isCompleted ? <Check size={16} strokeWidth={3} /> : step.number}
                </div>
                <span className={`text-[10px] lg:text-xs text-center max-w-[70px] lg:max-w-[80px] transition-colors leading-tight ${
                  isLocked ? 'text-red-400/50' : step.number === currentStep ? 'text-amber-400 font-semibold' : isClickable ? 'text-slate-400' : 'text-slate-600'
                }`}>
                  {step.title}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Progress Bar - Mobile */}
      <div className="md:hidden bg-slate-900/50 border border-slate-800 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-slate-400">Step {currentStep} of 9</span>
          <span className="text-sm font-medium text-amber-400">{steps[currentStep - 1].title}</span>
        </div>
        <div className="flex gap-1.5">
          {steps.map((step) => {
            const isClickable = isStepAccessible(step.number);
            const isLocked = isExpired && step.number !== 9;
            return (
              <button
                key={step.number}
                onClick={() => isClickable && handleStepClick(step.number)}
                disabled={!isClickable}
                className={`flex-1 h-8 rounded-lg transition-all flex items-center justify-center text-xs font-bold ${
                  isLocked ? 'bg-red-900/20 text-red-400/50 border border-red-500/20'
                    : step.number === currentStep ? 'bg-amber-500 text-slate-900'
                    : step.number < currentStep ? 'bg-amber-500/40 text-amber-200'
                    : isClickable ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                    : 'bg-slate-800 text-slate-600'
                } ${isClickable ? 'cursor-pointer active:scale-95' : 'cursor-not-allowed'}`}
              >
                {isLocked ? <Lock size={12} /> : step.number}
              </button>
            );
          })}
        </div>
      </div>

      {/* Step Header with Video Guide */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-bold text-white mb-3">
          Step {currentStep} of 9: {steps[currentStep - 1].title}
        </h2>
        {getCurrentStepVideo() && (
          <YouTubeEmbed
            videoUrl={getCurrentStepVideo().youtube_url}
            title={getCurrentStepVideo().video_title}
            stepNumber={currentStep}
          />
        )}
      </div>

      {/* Step Content */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 sm:p-6">
        <CurrentStepComponent
          data={willData}
          onNext={handleNext}
          onPrevious={handlePrevious}
          onSaveAndExit={handleSaveAndExit}
          onUpdate={handleUpdate}
          saving={saving}
          willId={willId}
          isPartnerMode={true}
        />
      </div>

      {/* Pre-Payment Disclaimer Modal */}
      <PrePaymentDisclaimerModal
        isOpen={showDisclaimerModal}
        onClose={handleDisclaimerClose}
        onAccept={handleDisclaimerAccept}
        disclaimerText={disclaimerText}
      />

      {/* Partner Will Payment Modal */}
      <PartnerWillPaymentModal
        isOpen={showPaymentModal}
        onClose={handlePaymentClose}
        onSuccess={handlePaymentSuccess}
        initialPrice={partnerPricing?.will_price}
        willId={willId}
      />

      {/* Processing Modal */}
      <WillProcessingModal
        isOpen={showProcessingModal}
        onComplete={handleProcessingComplete}
      />
    </div>
  );
};

export default PartnerWillCreation;
